import { usePelviaCare, noteStatusLine } from '@/hooks/usePelviaCare'
import PelviaCareComingSoon from './PelviaCareComingSoon'

/**
 * PelviaCare+ card on the patient homepage.
 *
 * When the PelviaCare+ master switch is off (`pc.available === false`),
 * non-members see the "coming soon" waitlist card instead of the paid flow.
 * When it's on, non-members see the $300 CTA (screener gate → Stripe checkout).
 * Once paid, the card flips to the member view regardless of the switch (so a
 * paid member is never stranded): a "PelviaCare+ member" badge, the consult
 * note's status, and a button to (re)send the note to their clinician — so the
 * whole flow lives on the homepage and never bounces the patient to Integrations.
 */
export default function ReportCard() {
  const pc = usePelviaCare()

  // Hold until the master switch is known, so we never flash the $300 CTA before
  // resolving to "coming soon" (or vice-versa) for a non-member.
  if (pc.available === undefined && !pc.isMember) return null

  // Feature gated off — tease it and offer the waitlist (paid members fall
  // through to their note view below).
  if (!pc.available && !pc.isMember) return <PelviaCareComingSoon />

  if (pc.isMember) {
    const line = noteStatusLine(pc.noteStatus, pc.noteSignedAt, pc.clinicianName)
    return (
      <>
        <section className="card pp-report-cta pp-member-card">
          <div className="pp-member-head">
            <span className="pp-member-badge">PelviaCare+ member</span>
          </div>
          <strong>Your consult note</strong>
          <p className={`pp-note-status pp-note-status-${line.tone}`}>{line.text}</p>
          {pc.message && <p className="pp-note-message">{pc.message}</p>}

          {pc.noteSigned ? (
            <button
              type="button"
              className="pp-report-btn"
              onClick={() => pc.downloadNote('pdf')}
              disabled={pc.downloading}
            >
              {pc.downloading ? 'Preparing…' : 'Download your consult note'}
            </button>
          ) : pc.canSend ? (
            <button
              type="button"
              className="pp-report-btn"
              onClick={pc.sendNote}
              disabled={pc.sendingNote}
            >
              {pc.sendingNote
                ? 'Sending…'
                : pc.noteStatus === 'error'
                  ? 'Try again'
                  : pc.noteStatus === 'needs_resubmit'
                    ? 'Resubmit my note'
                    : 'Send note to my clinician'}
            </button>
          ) : (
            // note_pushed — awaiting sign-off; nothing to re-send, but the patient
            // can look at the note their clinician is reviewing.
            <button type="button" className="pp-report-btn-secondary" onClick={pc.previewNote}>
              Preview my note
            </button>
          )}
        </section>
        {pc.modals}
      </>
    )
  }

  return (
    <>
      <section className="card pp-report-cta">
        <div className="pp-report-primary-row">
          <div className="pp-report-text">
            <span className="pp-report-eyebrow">PelviaCare+</span>
            <strong>Get your note reviewed &amp; signed — $300</strong>
            <p>
              A board-certified clinician reviews your Pelvia history and returns
              a signed consult note with your next step — within 72 hours.
            </p>
          </div>
          <button type="button" className="pp-report-btn" onClick={pc.startCheckout}>
            Get started
          </button>
        </div>
        <ul className="pp-report-benefits">
          <li>Clinician review of your full symptom &amp; pattern history</li>
          <li>A signed consult note for your insurance or referral</li>
          <li>Your next step — OB/GYN eval or surgery consult</li>
        </ul>
      </section>
      {pc.modals}
    </>
  )
}
